import type { QuestionType } from '@l2u/ui/lib/types.ts';

// Which question types offer the "Adoption documents" section, and what their
// rows serialise to. Extracted as pure functions (pattern:
// questionAiValidation.ts / documentRow.ts — the app has no DOM test env) so
// the section's render gate and toApiPayload can never disagree about which
// drafts keep their rows. They used to: the section rendered for the generic
// types while toApiPayload sent [] for every doc type, so rows added to a
// document_upload question were silently discarded on save.

export type DraftTrigger = {
  // null = unconditional adoption document (generic questions and
  // document_upload). The multiselect_uploads option-mapped flow stores its
  // option key here.
  trigger_value: string | null;
  document_id: string;
  adopt_only: boolean;
};

/**
 * Whether the builder shows the unconditional "Adoption documents" section for
 * this type.
 *
 * document_upload is included: the vendor keeps its own upload dropzone and the
 * linked standards render alongside it as reference documents to download, fill
 * in and upload back (activeAdoptionTargets tags them source:'trigger', which
 * renders read-only and — per progress.ts — never gates completion, so the
 * question is still satisfied by an uploaded file).
 *
 * Excluded: multiselect_uploads (maps a doc per answer option in its own
 * editor), document_adopt (legacy, keeps its single required dropdown), and the
 * types that carry no vendor answer to attach documents to.
 */
export function showsAdoptionDocsSection(type: QuestionType): boolean {
  return (
    type === 'yesno' ||
    type === 'text' ||
    type === 'select' ||
    type === 'multiselect' ||
    type === 'document_upload'
  );
}

/** The adoption_triggers array to send for a draft of this type. Always an
 *  array — the API replaces the whole set, so [] is what deletes cleared rows
 *  rather than leaving the old ones behind. */
export function adoptionTriggersPayload(type: QuestionType, rows: DraftTrigger[]): DraftTrigger[] {
  if (type === 'multiselect_uploads') {
    // Option-mapped rows still require both the option key and a doc.
    return rows.filter((t) => t.trigger_value && t.document_id);
  }
  if (!showsAdoptionDocsSection(type)) return [];
  // Unconditional rows: only a doc is required. An untouched Select is not a
  // link, so those rows are dropped rather than failing the API's FK check.
  return rows.filter((t) => t.document_id);
}
